import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import SyncIcon from "@material-ui/icons/Sync";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import ContactsIcon from "@material-ui/icons/Contacts";
import EditIcon from "@material-ui/icons/Edit";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  cnpjInput: {
    minWidth: 260,
  },
}));

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const Organizations = () => {
  const classes = useStyles();
  const history = useHistory();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cnpj, setCnpj] = useState("");
  const [search, setSearch] = useState("");
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    email: "",
    telefone: "",
    isActive: true,
  });

  const loadGroups = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/business-groups");
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleSyncCnpj = async () => {
    const cleanCnpj = onlyDigits(cnpj);
    if (cleanCnpj.length !== 14) {
      toast.warn("Informe um CNPJ valido com 14 digitos.");
      return;
    }

    try {
      setSyncing(true);
      await api.post("/organizations/sync-cnpj", { cnpj: cleanCnpj });
      toast.success("Organizacao sincronizada com sucesso.");
      setCnpj("");
      await loadGroups();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const organizations = useMemo(() => {
    const grouped = groups.flatMap((group) => {
      const organizationsInGroup = Array.isArray(group.organizations)
        ? group.organizations
        : [];

      return organizationsInGroup.map((organization) => ({
        ...organization,
        groupName: group.name,
      }));
    });

    const term = search.trim().toLowerCase();
    if (!term) return grouped;

    return grouped.filter((org) => {
      const bucket = [
        org.cnpj,
        org.nomeFantasia,
        org.razaoSocial,
        org.groupName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return bucket.includes(term);
    });
  }, [groups, search]);

  const openEditModal = (organization) => {
    if (!organization) return;
    setEditingOrganization(organization);
    setEditForm({
      cnpj: onlyDigits(organization.cnpj || ""),
      razaoSocial: organization.razaoSocial || "",
      nomeFantasia: organization.nomeFantasia || "",
      email: organization.email || "",
      telefone: organization.telefone || "",
      isActive: Boolean(organization.isActive),
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingOrganization(null);
  };

  const handleSaveOrganization = async () => {
    if (!editingOrganization) return;

    try {
      setSavingEdit(true);
      await api.put(`/organizations/${editingOrganization.id}`, {
        cnpj: onlyDigits(editForm.cnpj || ""),
        razaoSocial: editForm.razaoSocial,
        nomeFantasia: editForm.nomeFantasia,
        email: editForm.email || null,
        telefone: editForm.telefone || null,
        isActive: Boolean(editForm.isActive),
      });

      toast.success("Organizacao atualizada com sucesso.");
      closeEditModal();
      await loadGroups();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Title>Organizacoes</Title>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar por CNPJ, razao social, fantasia ou grupo"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  className={classes.cnpjInput}
                  label="CNPJ"
                  placeholder="Somente numeros"
                  value={cnpj}
                  onChange={(e) => setCnpj(onlyDigits(e?.target?.value || ""))}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  disabled={syncing}
                  onClick={handleSyncCnpj}
                  startIcon={<SyncIcon />}
                >
                  Sincronizar
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Grupo</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Razao Social</TableCell>
              <TableCell>Nome Fantasia</TableCell>
              <TableCell>Matriz/Filial</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && organizations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma organizacao encontrada.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {organizations.map((organization) => (
              <TableRow key={organization.id}>
                <TableCell>{organization.groupName}</TableCell>
                <TableCell>{organization.cnpj || "-"}</TableCell>
                <TableCell>{organization.razaoSocial || "-"}</TableCell>
                <TableCell>{organization.nomeFantasia || "-"}</TableCell>
                <TableCell>{organization.isHeadquarter ? "Matriz" : "Filial"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={organization.isActive ? "Ativa" : "Inativa"}
                    color={organization.isActive ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => openEditModal(organization)}
                    style={{ marginRight: 4 }}
                  >
                    <EditIcon color="secondary" fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    color="default"
                    startIcon={<ContactsIcon />}
                    onClick={() => history.push(`/organizations/${organization.id}/contacts`)}
                    style={{ marginRight: 8 }}
                  >
                    Contatos
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<AccountTreeIcon />}
                    onClick={() => history.push(`/organizations/${organization.id}/sectors`)}
                  >
                    Setores
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={editModalOpen} onClose={closeEditModal} fullWidth maxWidth="sm">
        <DialogTitle>Editar Organizacao</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CNPJ"
                value={editForm.cnpj}
                onChange={(e) => {
                  const value = onlyDigits(e?.target?.value || "");
                  setEditForm((prev) => ({ ...prev, cnpj: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Razao Social"
                value={editForm.razaoSocial}
                onChange={(e) => {
                  const value = e?.target?.value || "";
                  setEditForm((prev) => ({ ...prev, razaoSocial: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome Fantasia"
                value={editForm.nomeFantasia}
                onChange={(e) => {
                  const value = e?.target?.value || "";
                  setEditForm((prev) => ({ ...prev, nomeFantasia: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={editForm.email}
                onChange={(e) => {
                  const value = e?.target?.value || "";
                  setEditForm((prev) => ({ ...prev, email: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={editForm.telefone}
                onChange={(e) => {
                  const value = e?.target?.value || "";
                  setEditForm((prev) => ({ ...prev, telefone: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={Boolean(editForm.isActive)}
                    onChange={(e) => {
                      const checked = Boolean(e?.target?.checked);
                      setEditForm((prev) => ({ ...prev, isActive: checked }));
                    }}
                  />
                )}
                label="Ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} disabled={savingEdit}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSaveOrganization} disabled={savingEdit}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Organizations;